import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { CoinIcon } from '../../components/CoinIcon';
import { GlowBorder, MotionNumber, SlideToConfirm } from '../../components/ui';
import { useInfiniteActivity } from '../../hooks/useInfiniteActivity';
import { formatUsd, formatUsdSigned } from '../../lib/format';
import type {
  ActivityCounts,
  HlBalanceResponse,
  MyBalancesResponse,
  PmMarketForMe,
  PositionItem,
  PositionsResponse,
} from '../../lib/types';
import { AreaChart, Donut, PmPriceChart, Sparkline } from '../charts';
import { CabTabs } from '../tabs';
import {
  useEquitySeries,
  useLiveMarkets,
  type EquityRange,
  type MarketTick,
} from '../live';
import { mainWalletUsd, Panel } from '../parts';

const RANGES: EquityRange[] = ['24h', '7d', '30d', 'all'];

const RANGE_TO_MS: Record<EquityRange, number | null> = {
  '24h': 24 * 3600_000,
  '7d': 7 * 24 * 3600_000,
  '30d': 30 * 24 * 3600_000,
  all: null,
};

/* ── Equity hero ────────────────────────────────────────────────── */

export function EquityHero({
  balances,
  closed,
}: {
  balances: MyBalancesResponse | undefined;
  closed: PositionItem[] | undefined;
}) {
  const { t } = useTranslation();
  const [range, setRange] = useState<EquityRange>('7d');
  const [now] = useState(() => Date.now());
  const { points } = useEquitySeries(closed, range, now);

  // The per-period change comes from the server (realized PnL over the
  // whole window) — NOT the ~100 loaded closed positions, which for a
  // high-frequency user fit inside a day and made 7d/30d/all identical.
  const fromTs = useMemo(() => {
    const ms = RANGE_TO_MS[range];
    return ms == null ? undefined : new Date(now - ms).toISOString();
  }, [range, now]);
  const rangeQ = useInfiniteActivity({ type: 'trades', from_ts: fromTs });
  const change = Number(rangeQ.data?.pages[0]?.filtered_pnl ?? 0);

  const total =
    mainWalletUsd(balances) +
    Number(balances?.pm_bot_active.amount || 0) +
    Number(balances?.hl_bot_active.amount || 0);
  const up = change >= 0;

  return (
    <GlowBorder
      radius={20}
      speedSec={9}
      innerClassName="relative overflow-hidden p-5 sm:p-6"
    >
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-pb-accent/20 blur-3xl" />
      <img
        src="/whale-mascot.webp"
        alt=""
        aria-hidden
        width={150}
        className="pb-kit-bob pointer-events-none absolute -bottom-2 right-3 w-28 opacity-[0.16]"
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="pb-eyebrow">{t('cabinet.total_balance')}</div>
            <div className="pb-numeral mt-1 text-[40px] font-semibold leading-none text-tg-text sm:text-[46px]">
              <MotionNumber value={total} format="usd" />
            </div>
            <div className="mt-2 flex items-center gap-2 text-[13px]">
              <span
                className={clsx(
                  'pb-numeral inline-flex items-center gap-1 rounded-pb-full px-2 py-0.5 font-semibold',
                  up
                    ? 'bg-pb-success-soft text-pb-success'
                    : 'bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] text-pb-danger',
                )}
              >
                {up ? '▲' : '▼'} {formatUsdSigned(change)}
              </span>
              <span className="text-tg-hint">{t(`cabinet.range_${range}`)}</span>
            </div>
          </div>
          <div className="flex shrink-0 gap-1 rounded-pb-md border border-pb-border-soft bg-pb-paper2/60 p-1">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRange(r)}
                className={clsx(
                  'pb-focus-ring rounded-pb-sm px-2.5 py-1 text-[12px] font-medium transition-colors',
                  r === range
                    ? 'bg-pb-accent-soft text-pb-accent'
                    : 'text-tg-hint hover:text-tg-text',
                )}
              >
                {t(`cabinet.range_${r}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4">
          {points.length > 1 ? (
            <AreaChart values={points} positive={up} height={140} />
          ) : (
            <div className="grid h-[140px] place-items-center text-[13px] text-tg-hint">
              {t('cabinet.no_curve')}
            </div>
          )}
        </div>
      </div>
    </GlowBorder>
  );
}

/* ── Allocation donut ───────────────────────────────────────────── */

export function AllocationCard({
  balances,
}: {
  balances: MyBalancesResponse | undefined;
}) {
  const { t } = useTranslation();
  const main = mainWalletUsd(balances);
  const pm = Number(balances?.pm_bot_active.amount || 0);
  const hl = Number(balances?.hl_bot_active.amount || 0);
  const total = main + pm + hl;
  const segs = [
    { value: main, color: 'var(--pb-accent)', label: t('cabinet.tile_main') },
    { value: pm, color: 'var(--pb-accent-2)', label: t('cabinet.tile_pm') },
    { value: hl, color: 'var(--pb-success)', label: t('cabinet.tile_hl') },
  ];
  return (
    <Panel title={t('cabinet.allocation')}>
      <div className="flex items-center gap-5">
        <Donut
          segments={segs}
          center={
            <div>
              <div className="pb-caption">{t('cabinet.total_balance')}</div>
              <div className="pb-numeral text-[18px] font-semibold text-tg-text">
                {formatUsd(total)}
              </div>
            </div>
          }
        />
        <div className="flex-1 space-y-2.5">
          {segs.map((s) => (
            <div key={s.label} className="flex items-center gap-2 text-[13px]">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: s.color }}
              />
              <span className="flex-1 truncate text-tg-hint">{s.label}</span>
              <span className="pb-numeral text-tg-text">{formatUsd(s.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
}

/* ── Portfolio KPI strip ────────────────────────────────────────── */

export function KpiStrip({
  closed,
  counts,
}: {
  closed: PositionsResponse | undefined;
  counts: ActivityCounts | undefined;
}) {
  const { t } = useTranslation();
  // Win/loss/cancel from the server-side lifetime counts (not the
  // 20-item activity page).
  const won = counts?.trade_won ?? 0;
  const lost = counts?.trade_lost ?? 0;
  const canceled = counts?.trade_canceled ?? 0;
  const winRate = won + lost > 0 ? Math.round((won / (won + lost)) * 100) : 0;

  // Headline is PnL net of platform fees — what the user actually kept.
  const gross = Number(closed?.total_realized_pnl ?? 0);
  const feesPaid = Number(closed?.total_fees_paid ?? 0);
  const realized = Number(closed?.total_net_pnl ?? gross);
  const openCount = closed?.open_count ?? 0;

  // Sparkline from closed positions (oldest→newest cumulative realized).
  let acc = 0;
  const spark = (closed?.items ?? [])
    .filter((p) => p.realized_pnl_usdc != null && p.closed_at != null)
    .slice()
    .sort((a, b) => Date.parse(a.closed_at as string) - Date.parse(b.closed_at as string))
    .map((p) => (acc += Number(p.realized_pnl_usdc ?? 0)));

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Kpi
        label={t('cabinet.net_pnl')}
        value={<MotionNumber value={realized} format="usd-signed" />}
        tone={realized >= 0 ? 'pos' : 'neg'}
        sub={
          feesPaid > 0
            ? t('cabinet.pnl_before_fee', {
                gross: formatUsdSigned(gross),
                fee: formatUsd(feesPaid),
              })
            : undefined
        }
        viz={spark.length > 1 ? <Sparkline values={spark} positive={realized >= 0} /> : null}
      />
      <Kpi
        label={t('cabinet.win_rate')}
        value={`${winRate}%`}
        sub={
          <span className="flex items-center gap-2.5">
            <Tally glyph="✓" tone="success" n={won} />
            <Tally glyph="✕" tone="danger" n={lost} />
            <Tally glyph="⊘" tone="muted" n={canceled} />
          </span>
        }
        viz={<WinRing pct={winRate} />}
      />
      <Kpi label={t('cabinet.trades')} value={String(won + lost)} sub={t('cabinet.settled')} />
      <Kpi
        label={t('cabinet.open_positions')}
        value={String(openCount)}
        sub={t('cabinet.live_now')}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  viz,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: 'pos' | 'neg';
  viz?: React.ReactNode;
}) {
  return (
    <div className="rounded-pb-lg border border-pb-border-soft bg-pb-paper/60 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-pb-accent-line hover:shadow-pb-md">
      <div className="pb-caption">{label}</div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div
          className={clsx(
            'pb-numeral text-[22px] font-semibold leading-none',
            tone === 'pos'
              ? 'text-pb-success'
              : tone === 'neg'
                ? 'text-pb-danger'
                : 'text-tg-text',
          )}
        >
          {value}
        </div>
        {viz}
      </div>
      {sub ? <div className="pb-caption mt-1.5">{sub}</div> : null}
    </div>
  );
}

/** win / loss / cancel tally — colored glyph + count so the meaning
 * reads without a legend. */
function Tally({
  glyph,
  tone,
  n,
}: {
  glyph: string;
  tone: 'success' | 'danger' | 'muted';
  n: number;
}) {
  return (
    <span
      className={clsx(
        'pb-numeral inline-flex items-center gap-1 font-medium',
        tone === 'success'
          ? 'text-pb-success'
          : tone === 'danger'
            ? 'text-pb-danger'
            : 'text-tg-hint',
      )}
    >
      <span aria-hidden className="text-[12px] leading-none">{glyph}</span>
      {n}
    </span>
  );
}

function WinRing({ pct }: { pct: number }) {
  const r = 13;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" className="-rotate-90" aria-hidden>
      <circle cx="16" cy="16" r={r} fill="none" stroke="var(--pb-border-soft)" strokeWidth="4" />
      <circle
        cx="16"
        cy="16"
        r={r}
        fill="none"
        stroke="var(--pb-accent)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${c - dash}`}
        style={{ transition: 'stroke-dasharray 600ms ease' }}
      />
    </svg>
  );
}

/* ── Bot enable/disable toggle (section header) ─────────────────── */

export function BotToggle({
  enabled,
  available,
  paused,
  busy,
  onToggle,
}: {
  enabled: boolean;
  available: boolean;
  paused: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  const state = !enabled ? 'off' : paused ? 'paused' : 'active';
  const label =
    state === 'active'
      ? t('cabinet.bot_active')
      : state === 'paused'
        ? t('cabinet.bot_paused')
        : t('cabinet.bot_off');
  return (
    <div className="flex items-center gap-2.5">
      <span className="pb-caption">{label}</span>
      <SlideToConfirm
        on={enabled}
        busy={busy}
        disabled={!available || paused}
        ariaLabel={label}
        className="w-40"
        labelOff={t('cabinet.slide_to_enable')}
        labelOn={t('cabinet.slide_to_disable')}
        onConfirm={onToggle}
      />
    </div>
  );
}

export function BotPulse({ active }: { active: boolean }) {
  return (
    <span className="relative flex h-2.5 w-2.5">
      {active ? (
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pb-success opacity-60" />
      ) : null}
      <span
        className={clsx(
          'relative inline-flex h-2.5 w-2.5 rounded-full',
          active ? 'bg-pb-success' : 'bg-tg-hint',
        )}
      />
    </span>
  );
}

/* ── PM markets — Polymarket-event style + market tabs ──────────── */

export function PmMarketsTabbed({
  markets,
}: {
  markets: PmMarketForMe[] | undefined;
}) {
  const ticks = useLiveMarkets();
  const list = markets ?? [];
  const [sel, setSel] = useState<string | null>(null);
  const activeId =
    sel && list.some((m) => m.market_id === sel)
      ? sel
      : (list[0]?.market_id ?? null);
  const market = list.find((m) => m.market_id === activeId);
  const tick = activeId ? ticks[activeId] : undefined;
  if (!list.length) return null;

  return (
    <div>
      {/* smooth market tabs (like the webapp market selector) */}
      <CabTabs
        scroll
        value={activeId ?? ''}
        onChange={setSel}
        options={list.map((m) => {
          const tk = ticks[m.market_id];
          return {
            value: m.market_id,
            label: (
              <>
                <CoinIcon symbol={m.symbol} size={16} />
                {m.display_name}
                {tk ? (
                  <span className="pb-numeral text-[11px] opacity-70">
                    {Math.round(tk.upBid * 100)}%
                  </span>
                ) : null}
              </>
            ),
          };
        })}
      />

      {market ? <PmMarketDetail market={market} tick={tick} /> : null}
    </div>
  );
}

function useEpochCountdown(periodSec: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const periodMs = Math.max(1, periodSec) * 1000;
  const end = Math.ceil(now / periodMs) * periodMs;
  const rem = Math.max(0, end - now);
  return {
    mm: Math.floor(rem / 60000),
    ss: Math.floor((rem % 60000) / 1000),
    start: new Date(end - periodMs),
    end: new Date(end),
  };
}

function PmMarketDetail({
  market,
  tick,
}: {
  market: PmMarketForMe;
  tick?: MarketTick;
}) {
  const { t, i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const move = tick ? tick.oracle - tick.ptb : 0;
  const up = move >= 0;
  const tfMin = Math.round(market.timeframe_sec / 60);
  const { mm, ss, start, end } = useEpochCountdown(market.timeframe_sec);
  const fmt = (d: Date) =>
    d.toLocaleTimeString(ru ? 'ru-RU' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: !ru,
    });

  return (
    <div className="mt-3 rounded-pb-lg border border-pb-border-soft bg-pb-paper2/40 p-4 sm:p-5">
      {/* header: title + epoch window + countdown */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <CoinIcon symbol={market.symbol} size={36} />
          <div>
            <div className="text-[16px] font-semibold text-tg-text">
              {market.symbol} {t('cabinet.up_or_down')} {t('cabinet.tf', { min: tfMin })}
            </div>
            <div className="pb-caption">
              {fmt(start)}–{fmt(end)}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2 text-pb-warn">
          <TimeUnit n={mm} label={t('cabinet.min_short')} />
          <TimeUnit n={ss} label={t('cabinet.sec_short')} />
        </div>
      </div>

      {/* target vs current price */}
      <div className="mt-4 flex flex-wrap items-end gap-x-8 gap-y-3">
        <div>
          <div className="pb-caption">{t('cabinet.price_to_beat')}</div>
          <div className="pb-numeral text-[26px] font-semibold leading-none text-tg-hint">
            {tick ? formatUsd(tick.ptb) : '—'}
          </div>
        </div>
        <div className="border-l border-pb-border-soft pl-8">
          <div className="flex items-center gap-2">
            <span className="pb-caption">{t('cabinet.current_price')}</span>
            {tick ? (
              <span
                className={clsx(
                  'pb-numeral text-[11px] font-semibold',
                  up ? 'text-pb-success' : 'text-pb-danger',
                )}
              >
                {up ? '▲' : '▼'} {formatUsdSigned(move)}
              </span>
            ) : null}
          </div>
          <div className="pb-numeral text-[26px] font-semibold leading-none text-[#f0a04b]">
            {tick ? (
              <MotionNumber value={tick.oracle} format="usd" mode="smooth" />
            ) : (
              '—'
            )}
          </div>
        </div>
      </div>

      {/* price chart with the Price-to-beat target line */}
      <div className="mt-4">
        <PmPriceChart
          values={tick?.history ?? []}
          target={tick?.ptb ?? 0}
          targetLabel={t('cabinet.target')}
          height={200}
        />
      </div>

      {/* outcome token prices */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Outcome
          label={t('cabinet.outcome_up')}
          cents={tick ? Math.round(tick.upBid * 100) : null}
          tone="up"
        />
        <Outcome
          label={t('cabinet.outcome_down')}
          cents={tick ? Math.round(tick.downBid * 100) : null}
          tone="down"
        />
      </div>
    </div>
  );
}

function TimeUnit({ n, label }: { n: number; label: string }) {
  return (
    <div className="text-center">
      <div className="pb-numeral text-[26px] font-bold leading-none tabular-nums">
        {String(n).padStart(2, '0')}
      </div>
      <div className="pb-eyebrow mt-0.5 text-[9px] text-tg-hint">{label}</div>
    </div>
  );
}

function Outcome({
  label,
  cents,
  tone,
}: {
  label: string;
  cents: number | null;
  tone: 'up' | 'down';
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between rounded-pb-md border px-4 py-3',
        tone === 'up'
          ? 'border-pb-success/30 bg-pb-success-soft'
          : 'border-pb-danger/30 bg-[color-mix(in_oklab,var(--pb-danger)_12%,transparent)]',
      )}
    >
      <span
        className={clsx(
          'text-[14px] font-semibold',
          tone === 'up' ? 'text-pb-success' : 'text-pb-danger',
        )}
      >
        {label}
      </span>
      <span
        className={clsx(
          'pb-numeral text-[18px] font-bold',
          tone === 'up' ? 'text-pb-success' : 'text-pb-danger',
        )}
      >
        {cents == null ? '—' : `${cents}¢`}
      </span>
    </div>
  );
}

/* ── HL account summary ─────────────────────────────────────────── */

export function HlSummary({ hl }: { hl: HlBalanceResponse | undefined }) {
  const { t } = useTranslation();
  const unreal = (hl?.positions ?? []).reduce(
    (a, p) => a + Number(p.unrealized_pnl || 0),
    0,
  );
  return (
    <div className="grid grid-cols-3 gap-3">
      <HlStat label={t('cabinet.hl_account_value')} value={formatUsd(hl?.account_value ?? 0)} />
      <HlStat label={t('cabinet.hl_withdrawable')} value={formatUsd(hl?.withdrawable ?? 0)} />
      <HlStat
        label={t('cabinet.hl_unrealized')}
        value={formatUsdSigned(unreal)}
        tone={unreal >= 0 ? 'pos' : 'neg'}
      />
    </div>
  );
}

function HlStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'pos' | 'neg';
}) {
  return (
    <div className="rounded-pb-md border border-pb-border-soft bg-pb-paper2/50 p-3">
      <div className="pb-caption">{label}</div>
      <div
        className={clsx(
          'pb-numeral mt-1 text-[16px] font-semibold',
          tone === 'pos'
            ? 'text-pb-success'
            : tone === 'neg'
              ? 'text-pb-danger'
              : 'text-tg-text',
        )}
      >
        {value}
      </div>
    </div>
  );
}
